import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Search, AlertTriangle, CheckCircle2, XCircle, User, CreditCard, Copy, Check } from "lucide-react";
import { BRANCH_META } from "@/lib/branches";

// Same key as AdminEmails, so admins only paste the token once per browser.
const TOKEN_KEY = "atlas-admin-api-token";

export default function AdminLookup() {
    const { user } = useAuth();
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [tokenDraft, setTokenDraft] = useState("");
    const [email, setEmail] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copied, setCopied] = useState(false);

    const isAdmin =
        user &&
        (user.role === "admin" ||
            user.email === "admin@atlas.app" ||
            user.email === "purposelyy@gmail.com");

    const saveToken = () => {
        const t = tokenDraft.trim();
        if (!t) return;
        localStorage.setItem(TOKEN_KEY, t);
        setToken(t);
        setTokenDraft("");
    };

    const lookup = async (e) => {
        if (e) e.preventDefault();
        const q = email.trim();
        if (!q || !token) return;
        setLoading(true);
        setError(null);
        setData(null);
        try {
            const { data } = await api.get(`/admin/diagnostics/${encodeURIComponent(q)}`, {
                headers: { "X-Admin-Token": token },
            });
            setData(data);
        } catch (e) {
            setError(e.response?.data?.detail || "Lookup failed — token invalid or user not found.");
        } finally {
            setLoading(false);
        }
    };

    const copyJson = () => {
        navigator.clipboard.writeText(JSON.stringify(data, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    if (user && !isAdmin) return <Navigate to="/atlas" replace />;
    if (!user) return null;

    // Derive a plain-English paid/unpaid verdict from the diagnostics payload.
    const verdict = (() => {
        if (!data) return null;
        const u = data.user;
        if (!u) return { ok: false, label: "No user account with this email", tone: "amber" };
        if (u.subscription_status === "active" || u.subscription_status === "trialing") {
            return { ok: true, label: `PAID · ${u.subscription_status}`, tone: "green" };
        }
        // Also detect a live Stripe subscription even if our DB is out of sync.
        const liveActive = (data.stripe_customers || []).some((c) =>
            (c.subscriptions || []).some((s) => ["active", "trialing"].includes(s.status))
        );
        if (liveActive) {
            return {
                ok: true,
                label: "PAID on Stripe · DB out of sync",
                tone: "amber",
            };
        }
        return { ok: false, label: `Not paid · ${u.subscription_status || "no subscription"}`, tone: "red" };
    })();

    const toneClasses = {
        green: "bg-emerald-50 text-emerald-800 border-emerald-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        red: "bg-rose-50 text-rose-800 border-rose-200",
    };

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-4xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <p
                    className="chip"
                    style={{
                        background: BRANCH_META.humanities.soft,
                        color: BRANCH_META.humanities.text,
                    }}
                >
                    <Search className="w-3 h-3" /> Admin · User Lookup
                </p>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-2 leading-none">
                    Who's paid, and who isn't.
                </h1>
                <p className="text-slate-600 mt-2 max-w-2xl">
                    Type any email. We'll show whether they have an account, an active subscription,
                    and their payment history — pulled live from Mongo + Stripe.
                </p>

                {!token && (
                    <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="crystal rounded-3xl p-6 mt-6"
                        data-testid="admin-lookup-token-gate"
                    >
                        <div className="flex items-center gap-2 text-amber-800">
                            <AlertTriangle className="w-4 h-4" />
                            <div className="font-display font-bold">
                                Paste your admin API token to enable lookups
                            </div>
                        </div>
                        <p className="text-sm text-slate-600 mt-2">
                            One-time. Stored in this browser's local storage. Value comes from{" "}
                            <code>ADMIN_API_TOKEN</code> in <code>/app/backend/.env</code>.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <input
                                type="password"
                                value={tokenDraft}
                                onChange={(e) => setTokenDraft(e.target.value)}
                                placeholder="ip_yonrmXFEm8h…"
                                className="field-input flex-1 min-w-[240px] font-mono text-sm"
                                data-testid="admin-lookup-token-input"
                            />
                            <button
                                onClick={saveToken}
                                className="arcade-btn"
                                data-variant="primary"
                                data-testid="admin-lookup-token-save"
                            >
                                Save
                            </button>
                        </div>
                    </motion.div>
                )}

                {token && (
                    <form onSubmit={lookup} className="crystal rounded-3xl p-6 mt-6 flex flex-wrap gap-2 items-center">
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="user@example.com"
                            className="field-input flex-1 min-w-[240px]"
                            required
                            data-testid="admin-lookup-email-input"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="arcade-btn"
                            data-variant="primary"
                            data-testid="admin-lookup-submit"
                        >
                            {loading ? "Looking up…" : "Look up"}
                        </button>
                    </form>
                )}

                {error && (
                    <div
                        className="mt-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 text-sm"
                        data-testid="admin-lookup-error"
                    >
                        {error}
                    </div>
                )}

                {data && (
                    <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-6 space-y-4"
                        data-testid="admin-lookup-result"
                    >
                        {/* Verdict */}
                        <div
                            className={`crystal rounded-3xl p-5 border-2 ${toneClasses[verdict.tone]}`}
                            data-testid="admin-lookup-verdict"
                        >
                            <div className="flex items-center gap-3">
                                {verdict.ok ? (
                                    <CheckCircle2 className="w-6 h-6" />
                                ) : (
                                    <XCircle className="w-6 h-6" />
                                )}
                                <div>
                                    <div className="font-display font-black text-2xl tracking-tighter">
                                        {verdict.label}
                                    </div>
                                    <div className="text-xs opacity-80 mt-0.5 font-mono">
                                        {data.queried_email}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* User doc */}
                        {data.user && (
                            <div className="crystal rounded-3xl p-5">
                                <div className="flex items-center gap-2 mb-3">
                                    <User className="w-4 h-4 text-humanities" />
                                    <div className="font-display font-bold text-slate-900">
                                        Account
                                    </div>
                                </div>
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                    <Row label="Name" value={data.user.name} />
                                    <Row label="Username" value={data.user.username} />
                                    <Row label="Subscription status" value={data.user.subscription_status || "—"} />
                                    <Row label="First quest done" value={String(data.user.first_quest_done)} />
                                    <Row label="Stripe customer" value={data.user.stripe_customer_id || "—"} mono />
                                    <Row label="Stripe subscription" value={data.user.stripe_subscription_id || "—"} mono />
                                    <Row label="Sub started" value={data.user.subscription_started_at} mono />
                                    <Row label="Sub updated" value={data.user.subscription_updated_at} mono />
                                    <Row label="Manually granted" value={data.user.manually_granted_at || "—"} mono />
                                    <Row label="Created" value={data.user.created_at} mono />
                                </dl>
                            </div>
                        )}

                        {/* Stripe customers */}
                        {data.stripe_customers && data.stripe_customers.length > 0 && (
                            <div className="crystal rounded-3xl p-5" data-testid="admin-lookup-stripe">
                                <div className="flex items-center gap-2 mb-3">
                                    <CreditCard className="w-4 h-4 text-humanities" />
                                    <div className="font-display font-bold text-slate-900">
                                        Stripe customers ({data.stripe_customers.length})
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    {data.stripe_customers.map((c) => (
                                        <div key={c.id} className="rounded-2xl bg-white/60 p-3 border border-slate-200">
                                            <div className="text-xs font-mono text-slate-500">{c.id}</div>
                                            <div className="text-sm text-slate-800">{c.email}</div>
                                            <div className="text-xs text-slate-500 mt-1">
                                                {c.subscriptions.length === 0
                                                    ? "No subscriptions"
                                                    : c.subscriptions
                                                          .map((s) => `${s.status}`)
                                                          .join(" · ")}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Payment tx history */}
                        {data.payment_transactions && data.payment_transactions.length > 0 && (
                            <div className="crystal rounded-3xl p-5">
                                <div className="font-display font-bold text-slate-900 mb-3">
                                    Payment transactions ({data.payment_transactions.length})
                                </div>
                                <div className="space-y-2">
                                    {data.payment_transactions.map((tx, i) => (
                                        <div key={i} className="text-xs bg-white/60 rounded-xl p-2 font-mono">
                                            <div>
                                                <span className="text-slate-500">status:</span>{" "}
                                                {tx.status || "—"} · <span className="text-slate-500">amount:</span>{" "}
                                                {tx.amount ? `$${(tx.amount / 100).toFixed(2)}` : "—"}
                                            </div>
                                            <div className="text-slate-500">session: {tx.session_id}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Raw JSON dump for copy/paste to Stripe support etc. */}
                        <details className="crystal rounded-3xl p-5">
                            <summary className="font-display font-bold text-slate-900 cursor-pointer flex items-center justify-between">
                                <span>Raw diagnostics JSON</span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        copyJson();
                                    }}
                                    className="text-xs inline-flex items-center gap-1 text-humanities"
                                >
                                    {copied ? (
                                        <>
                                            <Check className="w-3 h-3" /> Copied
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="w-3 h-3" /> Copy
                                        </>
                                    )}
                                </button>
                            </summary>
                            <pre className="mt-3 text-[11px] leading-tight bg-slate-900 text-cream p-3 rounded-xl overflow-auto max-h-96">
                                {JSON.stringify(data, null, 2)}
                            </pre>
                        </details>
                    </motion.div>
                )}
            </div>
        </div>
    );
}

function Row({ label, value, mono }) {
    return (
        <div className="flex justify-between gap-2 border-b border-slate-200/60 py-1">
            <dt className="text-slate-500">{label}</dt>
            <dd className={`text-slate-900 text-right ${mono ? "font-mono text-xs" : ""}`}>
                {value === undefined || value === null || value === "" ? "—" : String(value)}
            </dd>
        </div>
    );
}
